import type { Services } from "../data/services";
import { initData, telegramUser } from "../platform/telegram";
import {
  FROSHBOT_GATEWAY_URL,
  REAL_TOOL_CATALOG,
  TOOL_CATALOG,
  TURN_TIMEOUT_MS,
} from "./constants";
import { invalidate, validIdToken } from "./cognito";
import { assembleContext } from "./contextAssembler";
import { extractMarkers } from "./markers";
import type { ChatMessage, FroshBotEngine, TurnEvent } from "./types";
import { makeMessage } from "./types";

/**
 * The live gateway client — a browser-WebSocket port of the iOS
 * `LiveFroshBotService`, minus vision (no screenshot path on this surface).
 *
 * One socket, reconnected lazily. A rejected handshake surfaces on the first
 * send rather than at connect, so the retry path assumes a stale token, mints
 * a fresh one, and gives it exactly one more go.
 */

interface ServerFrame {
  type?: string;
  content?: string;
  status?: string;
  error?: string;
}

function makeSessionId(): string {
  return `miniapp-${telegramUser()?.id ?? "dev"}-${crypto.randomUUID()}`;
}

export class LiveFroshBot implements FroshBotEngine {
  readonly live = true;
  readonly history: ChatMessage[] = [];

  private ws: WebSocket | null = null;
  private contextSent = false;
  private sessionId = makeSessionId();

  constructor(private services: Services) {}

  private async connect(): Promise<WebSocket> {
    // Inside Telegram the signed launch payload IS the credential: the gateway
    // validates it and the session runs as the real `telegram:<id>` user — the
    // same identity the bot has in DMs. The compiled-in demo Cognito login is
    // the browser-dev path only.
    const signed = initData();
    const token = signed ? `tma:${signed}` : await validIdToken();
    const ws = new WebSocket(`${FROSHBOT_GATEWAY_URL}?token=${encodeURIComponent(token)}`);
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("error", onFail);
        ws.removeEventListener("close", onFail);
      };
      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onFail = () => {
        cleanup();
        reject(new Error("Froshbot is offline"));
      };
      ws.addEventListener("open", onOpen);
      ws.addEventListener("error", onFail);
      ws.addEventListener("close", onFail);
    });
    return ws;
  }

  private async socket(): Promise<WebSocket> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return this.ws;
    try {
      this.ws = await this.connect();
    } catch {
      // Usually a stale token refused at $connect. Once more with a fresh one.
      invalidate();
      this.ws = await this.connect();
    }
    return this.ws;
  }

  private async buildMessage(content: string): Promise<string> {
    if (this.contextSent) return content;
    this.contextSent = true;
    // Real identity (tma) never gets the mock snapshot (FOLLOWUPS 8.10): the
    // server-threaded profile is the single identity source, and prepending
    // Alex Chen's demo data made the bot greet real users with mock facts.
    // The demo snapshot is browser-dev only, where the shared Cognito demo
    // user and the in-bundle mocks are the same fiction.
    if (initData()) {
      return `[Context: currentScreen: froshbot]\n\n${content}`;
    }
    const snapshot = await assembleContext(this.services);
    return `[Context: currentScreen: froshbot]\n\n${snapshot}\n\n---\n\n${content}`;
  }

  async sendTurn(
    content: string,
    opts: { hidden?: boolean },
    onEvent: (event: TurnEvent) => void,
  ): Promise<void> {
    this.history.push(makeMessage({ role: "user", content, hidden: opts.hidden }));

    let ws: WebSocket;
    let message: string;
    try {
      message = await this.buildMessage(content);
      ws = await this.socket();
      ws.send(
        JSON.stringify({
          action: "sendMessage",
          message,
          sessionId: this.sessionId,
          toolCatalog: initData() ? REAL_TOOL_CATALOG : TOOL_CATALOG,
        }),
      );
    } catch {
      onEvent({
        kind: "error",
        message: "I lost my train of thought — connection dropped. Your message is saved.",
      });
      return;
    }

    let reply = "";
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        ws.removeEventListener("message", onMessage);
        ws.removeEventListener("close", onClose);
        resolve();
      };

      const timer = setTimeout(() => {
        onEvent({ kind: "error", message: "Froshbot took too long to answer. Try again." });
        ws.close();
        finish();
      }, TURN_TIMEOUT_MS);

      const onMessage = (raw: MessageEvent) => {
        let frame: ServerFrame;
        try {
          frame = JSON.parse(String(raw.data)) as ServerFrame;
        } catch {
          return;
        }
        // Frames without a type include API Gateway's own 29-second
        // "Endpoint request timed out" notice — the real reply arrives out of
        // band, so skipping these is what keeps long tool turns alive.
        if (!frame.type) return;

        switch (frame.type) {
          case "message":
          case "stream_chunk": {
            if (!frame.content) break;
            const { events, clean } = extractMarkers(frame.content);
            for (const event of events) onEvent(event);
            if (clean.length > 0) reply += reply.length === 0 ? clean : `\n${clean}`;
            break;
          }
          case "error":
            onEvent({ kind: "error", message: frame.error ?? "Froshbot is offline" });
            finish();
            break;
          case "status":
            // Casing is inconsistent across the gateway's own call sites.
            if (frame.status?.toLowerCase() === "idle") finish();
            break;
          default:
            break;
        }
      };

      const onClose = () => {
        if (!done) {
          onEvent({
            kind: "error",
            message: "I lost my train of thought — connection dropped. Your message is saved.",
          });
        }
        finish();
      };

      ws.addEventListener("message", onMessage);
      ws.addEventListener("close", onClose);
    });

    if (reply.length > 0) {
      this.history.push(makeMessage({ role: "assistant", content: reply }));
    }
  }
}
